import { useEffect, useState, type JSX } from 'react';
import { api, type StudentEventView } from '../../api.ts';
import { dayLabel } from '../../lib/people.ts';

/**
 * What has happened with one pupil, newest first.
 *
 * Under their name rather than in a feed of its own. A single stream of everything everybody did
 * answers a question nobody asks and buries the one that matters — this child, this week, what
 * changed. Loaded when the row is opened, because a class of thirty should not fetch thirty
 * histories to draw a list of names.
 */

const WORDS: Record<StudentEventView['kind'], string> = {
  invited: 'Отправлено приглашение',
  joined: 'Присоединился к школе',
  signed_in: 'Вошёл в кабинет',
  submitted: 'Сдал работу',
  quiz_finished: 'Прошёл тест',
  lesson_done: 'Завершил урок',
  certified: 'Получил сертификат',
};

/** The events worth a colour: something arrived that a teacher may need to act on. */
const NOTABLE: ReadonlySet<StudentEventView['kind']> = new Set(['submitted', 'certified']);

function clock(iso: string): string {
  return new Date(iso).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

export function StudentEvents({ profileId }: { profileId: string }): JSX.Element {
  const [events, setEvents] = useState<StudentEventView[] | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;

    api.authoring
      .studentEvents(profileId)
      .then((rows) => alive && setEvents(rows))
      .catch((cause: unknown) =>
        alive
          ? setError(cause instanceof Error ? cause.message : 'Не удалось загрузить события')
          : undefined,
      );

    return () => {
      alive = false;
    };
  }, [profileId]);

  if (error) return <p className="error">{error}</p>;
  if (events === null) return <p className="muted">Загружаем события…</p>;

  if (events.length === 0) {
    return (
      <div className="events">
        <h4 className="events__title">События</h4>
        <p className="muted">Пока ничего не происходило.</p>
      </div>
    );
  }

  return (
    <div className="events">
      <h4 className="events__title">События</h4>

      <ol className="events__list">
        {events.map((event, index) => {
          // A day heading only where the day changes, so a busy afternoon reads as one afternoon.
          const previous = events[index - 1];
          const opensADay = !previous || dayLabel(previous.at) !== dayLabel(event.at);

          return (
            <li key={`${event.kind}-${event.at}-${index}`}>
              {opensADay ? <div className="events__day">{dayLabel(event.at)}</div> : null}

              <div
                className={`events__row${NOTABLE.has(event.kind) ? ' events__row--notable' : ''}`}
              >
                <span className="events__time">{clock(event.at)}</span>
                <span className="events__what">{WORDS[event.kind]}</span>
                {event.subject ? <span className="events__subject">{event.subject}</span> : null}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
